// src/components/MediaPlayer.tsx
//
// One player for every lesson video, evidence video and voice note,
// wrapping a plain <video>/<audio> element with Plyr's controls.
// Sources are short-lived signed R2 urls fetched just before playing.
//
// The media element is created here, not rendered by React: Plyr wraps it
// and, on destroy, swaps it for a fresh copy. If React owned the element,
// its ref would point at a detached node after the first destroy (React's
// development double-mount does exactly that) and Plyr would attach to nothing.

import { useEffect, useRef } from "react";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

const VIDEO_CONTROLS = [
  "play-large",
  "play",
  "progress",
  "current-time",
  "duration",
  "mute",
  "volume",
  "captions",
  "settings",
  "pip",
  "fullscreen",
];
const AUDIO_CONTROLS = ["play", "progress", "current-time", "duration", "mute", "settings"];

export default function MediaPlayer({
  src,
  kind = "video",
  autoPlay = false,
  onPlay,
  className = "",
  captionsUrl = null,
  poster = null,
  speed = 1,
  rememberSpeed,
}: {
  src: string;
  kind?: "video" | "audio";
  autoPlay?: boolean;
  onPlay?: () => void;
  className?: string;
  /** WebVTT captions (lesson videos). Off until the viewer turns them on. */
  captionsUrl?: string | null;
  poster?: string | null;
  /** Starting playback speed. Lesson videos start slower (0.85) so the narration is easy to follow. */
  speed?: number;
  /** A name to remember the viewer's own speed choice under, on this device */
  rememberSpeed?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const onPlayRef = useRef(onPlay);
  onPlayRef.current = onPlay;

  useEffect(() => {
    const container = box.current;
    if (!container) return;
    let startSpeed = speed;
    try {
      const saved = rememberSpeed ? Number(localStorage.getItem(rememberSpeed)) : NaN;
      if (saved >= 0.5 && saved <= 2) startSpeed = saved;
    } catch {
      /* storage unavailable */
    }

    const media = document.createElement(kind);
    // Slower narration keeps its natural voice (no deeper pitch)
    media.preservesPitch = true;
    // Captions live in storage on another origin, so the media must be
    // fetched with CORS for the browser to load the track.
    if (captionsUrl) media.crossOrigin = "anonymous";
    media.src = src;
    media.preload = "metadata";
    if (media instanceof HTMLVideoElement) {
      media.playsInline = true;
      if (poster) media.poster = poster;
    }
    if (captionsUrl) {
      const track = document.createElement("track");
      track.kind = "captions";
      track.label = "English";
      track.srclang = "en";
      track.src = captionsUrl;
      media.appendChild(track);
    }
    container.appendChild(media);

    const player = new Plyr(media, {
      controls: kind === "audio" ? AUDIO_CONTROLS : VIDEO_CONTROLS,
      settings: captionsUrl ? ["captions", "speed"] : ["speed"],
      captions: { active: false, language: "en", update: false },
      speed: { selected: startSpeed, options: [0.75, 0.85, 1, 1.25, 1.5] },
      autoplay: autoPlay,
      resetOnEnd: true,
      // Plyr's storage saves the speed on every rate change, even the 1x it
      // sets itself on load, which would override the starting speed for
      // anyone who has played a video before. Each video starts fresh.
      storage: { enabled: false },
    });
    const handler = () => onPlayRef.current?.();
    player.on("play", handler);
    // Plyr resets the rate when the media loads, so apply the starting speed
    // once it is ready (a speed the viewer picks during the video still wins)
    let applied = false;
    const applySpeed = () => {
      player.speed = startSpeed;
      applied = true;
    };
    player.once("ready", applySpeed);
    player.once("loadedmetadata", applySpeed);
    // The viewer's own choice in the speed menu is remembered for next time
    const onRate = () => {
      if (!applied || !rememberSpeed) return;
      try {
        localStorage.setItem(rememberSpeed, String(player.speed));
      } catch {
        /* storage unavailable */
      }
    };
    player.on("ratechange", onRate);

    return () => {
      player.off("ratechange", onRate);
      player.off("play", handler);
      player.destroy();
      container.innerHTML = "";
    };
  }, [src, kind, autoPlay, captionsUrl, poster, speed, rememberSpeed]);

  return <div ref={box} className={`fsl-player overflow-hidden rounded-2xl bg-black ${className}`} />;
}
